/**
 * Onboarding Context
 * 
 * Provides onboarding state and methods throughout the app.
 * Handles coordination between the onboarding overlay and trigger elements.
 */

import { createContext, useContext, useState, useCallback } from 'react'
import { useOnboarding, ONBOARDING_STEPS } from '../hooks/useOnboarding'

const OnboardingContext = createContext(null)

/**
 * OnboardingProvider - Wraps the app to provide onboarding state
 */
export function OnboardingProvider({ children }) {
  const onboarding = useOnboarding()
  const [highlightedElement, setHighlightedElement] = useState(null)
  const [entityCounts, setEntityCounts] = useState({
    projects: 0,
    characters: 0,
    events: 0,
    locations: 0,
  })

  /**
   * Update entity counts for skip condition checks
   */
  const updateEntityCounts = useCallback((counts) => {
    setEntityCounts(prev => ({ ...prev, ...counts }))
  }, [])

  /**
   * Check if a step should be skipped based on conditions
   */
  const shouldSkipStep = useCallback((step) => {
    if (!step?.skipCondition) return false

    switch (step.skipCondition) {
      case 'hasProject':
        return entityCounts.projects > 0
      case 'hasCharacter':
        return entityCounts.characters > 0
      case 'hasEvent':
        return entityCounts.events > 0
      default:
        return false
    }
  }, [entityCounts])

  /**
   * Get the effective current step (accounting for skipped steps)
   */
  const getEffectiveStep = useCallback(() => {
    if (!onboarding.isActive) return null

    let stepIndex = onboarding.currentStepIndex
    let step = ONBOARDING_STEPS[stepIndex]

    // Auto-advance through skippable steps
    while (step && shouldSkipStep(step) && stepIndex < ONBOARDING_STEPS.length - 1) {
      stepIndex++
      step = ONBOARDING_STEPS[stepIndex]
    }

    return step
  }, [onboarding.isActive, onboarding.currentStepIndex, shouldSkipStep])

  /**
   * Handle completing current step with auto-skip logic
   */
  const handleCompleteStep = useCallback(async () => {
    const currentStep = onboarding.currentStep
    if (!currentStep) return

    await onboarding.completeStep(currentStep.id)

    // Check if next step should be auto-skipped
    const nextIndex = onboarding.currentStepIndex + 1
    if (nextIndex < ONBOARDING_STEPS.length) {
      const nextStep = ONBOARDING_STEPS[nextIndex]
      if (shouldSkipStep(nextStep)) {
        // Auto-complete skipped step
        setTimeout(() => {
          onboarding.completeStep(nextStep.id)
        }, 100)
      }
    }
  }, [onboarding, shouldSkipStep])

  /**
   * Highlight an element for the current step
   */
  const highlightElement = useCallback((element) => {
    setHighlightedElement(element)
  }, [])

  /**
   * Clear the highlighted element
   */
  const clearHighlight = useCallback(() => {
    setHighlightedElement(null)
  }, [])

  const value = {
    // From useOnboarding
    ...onboarding,
    
    // Additional state
    highlightedElement,
    entityCounts,
    
    // Methods
    updateEntityCounts,
    shouldSkipStep,
    getEffectiveStep,
    handleCompleteStep,
    highlightElement,
    clearHighlight,
  }

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  )
}

/**
 * Hook to use onboarding context
 */
export function useOnboardingContext() {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error('useOnboardingContext must be used within an OnboardingProvider')
  }
  return context
}

export default OnboardingContext
